import {
    useI18n,
    useVueRouter,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import { computed } from '@vue/composition-api';

import { SportOfferUIStore } from '@store/modules/ui/sportOffer/sports/sportsIndex';
import { ALL_ID } from '@store/modules/ui/sportOffer/constants';
import { PredefinedItems } from '@store/modules/ui/sportOffer/sports/enums';
import { NavigationDataStore } from '@store/modules/navigation';
import {
    getLiteMode,
    omitSports,
} from '@modules/layout/header/utils/metaWhitelist';
import { slugify } from '@src/app/utils/slugify';
import { RouteName } from '@src/app/router/constants/RouteName';
import { TimeFilterSlug } from '@store/modules/ui/shared/timeFilter/utils';

export function useSports() {
    const { translate } = useI18n();
    const { route, router } = useVueRouter();

    const sportsStore = useVuexStore<SportOfferUIStore>('ui/sportOffer/sports');
    const navigationStore = useVuexStore<NavigationDataStore>('navigation');

    const tree = sportsStore.getter('tree');
    const treeBreadcrumbsIds = sportsStore.getter('treeBreadcrumbsIds');
    const isOnSportLivePage = navigationStore.getter('isOnSportLivePage');
    const setSelectedSportSidebarItem = sportsStore.action('setSelectedSportSidebarItem');

    const sports = computed(() => {
        let sportNodes = tree.value.map((node) => ({
            id: node.id,
            name: node.name,
            urlName: node.urlName,
            order: node.order,
            icon: node.icon?.replace(/^sds-icon-/, ''),
        })).filter((node) => node.id !== ALL_ID);

        if (getLiteMode()) {
            sportNodes = omitSports(sportNodes);
        }

        return isOnSportLivePage.value
            ? [
                {
                    id: ALL_ID,
                    name: translate(PredefinedItems.all).toString(),
                    icon: 'actions-sort-alphabet',
                },
                ...sportNodes,
            ] : sportNodes;
    });

    const selectedSportId = computed(() => treeBreadcrumbsIds.value[0]);

    function setSelectedSport(sportId: number) {
        setSelectedSportSidebarItem({
            selectedTreeItems: [
                sports.value.find((sport) => sport.id === sportId),
            ],
        });
    }

    function resolveSportRoute(sportId: number) {
        const sport = tree.value.find((node) => node.id === sportId);
        const sportRouteTabs = [RouteName.Sport.Social, RouteName.Sport.CategoryPerTime, RouteName.Sport.SportCompetitions] as const;
        const routeName = sportRouteTabs.includes(route.value.name!)
            ? route.value.name!
            : RouteName.Sport.CategoryPerTime;

        const params: Record<string, string> = {
            ...route.value.params,
            sportSlug: slugify(sport?.urlName ?? ''),
            timeFilter: TimeFilterSlug.Today,
        };

        delete params.categorySlug;

        return router.value.resolve({
            name: routeName,
            params,
        });
    }

    function resolveSportLink(sportId: number) {
        return resolveSportRoute(sportId).href;
    }

    return {
        allSportsId: ALL_ID,
        sports,
        selectedSportId,
        resolveSportLink,
        setSelectedSport,
        resolveSportRoute,
    };
}
